import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Difficulty, Grid } from './types';
import { useProfile, useSettings, useHistory, useSlogan, useGame, useAuth } from './hooks';
import { useMultiplayer, api } from './hooks/useMultiplayer';
import { getSocket, disconnectSocket } from './utils/socket';
import { Menu, Game, Settings, History, HowToPlay, Legal, Auth, ResetPassword, Leaderboard, Security } from './components/views';
import { supabase } from './utils/supabase';
import { CookieConsent } from './components/CookieConsent';
import { initAnalytics, trackEvent } from './utils/analytics';
import { I18nProvider, useTranslation } from './i18n';
import { isTutorialCompleted, markTutorialSkipped } from './hooks/useTutorial';
import TutorialGame from './components/views/TutorialGame';

const INITIAL_GRID: Grid = [[null, null, null], [null, null, null], [null, null, null]];

// ─── Shared App State (context-free: lifted to App) ─────────────────────────

const App: React.FC = () => {
  const [roomId, setRoomId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty>('MEDIUM');
  const [expChange, setExpChange] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const { profile, setProfile, addExp, refreshProfile, expThreshold, expProgress } = useProfile();
  const { settings, setSettings, applyTheme } = useSettings();
  const { history, loading: historyLoading, refreshHistory } = useHistory(playerId);
  const currentSlogan = useSlogan();
  const auth = useAuth();

  // ── Apply theme ──────────────────────────────────────────────────────────
  useEffect(() => {
    applyTheme();
  }, [settings.theme, applyTheme]);

  // ── Initialise analytics (loads gtag only if previously accepted) ──────
  useEffect(() => {
    initAnalytics();
  }, []);

  // ── Fetch full profile from DB for all users (anonymous + registered) ────
  // Track which auth user the profile was loaded for, so the name-sync effect
  // never writes a stale anonymous name to an authenticated user's row.
  const nameLoadedForAuthId = useRef<string | null>(null);
  // Track the name that was loaded from DB, so we only sync back to DB when
  // the user explicitly changes it (not when we just loaded it).
  const dbLoadedName = useRef<string | null>(null);

  useEffect(() => {
    if (auth.isLoading || !auth.user) return;

    // Capture the auth user ID at the time this effect runs so async
    // callbacks can detect if the auth user changed while they were waiting.
    const currentAuthId = auth.user.id;

    // Auth user changed — immediately invalidate the sync guard so the
    // name-sync useEffect cannot fire until the new profile is loaded.
    if (nameLoadedForAuthId.current !== currentAuthId) {
      nameLoadedForAuthId.current = null;
      dbLoadedName.current = null;
    }

    const fetchPlayer = async (attempt = 1): Promise<void> => {
      // Small delay to let the DB trigger / server-side creation complete
      await new Promise((r) => setTimeout(r, attempt === 1 ? 300 : 800));

      // Bail if the auth user changed while we were waiting
      if (auth.user?.id !== currentAuthId) return;

      const { data, error } = await supabase
        .from('players')
        .select('id, name, level, exp')
        .eq('auth_user_id', currentAuthId)
        .single();

      if (error || !data) {
        // Player row might not exist yet — retry up to 5 times
        if (attempt < 5) {
          console.log(`[App] Player not found yet, retrying (${attempt}/5)...`);
          return fetchPlayer(attempt + 1);
        }
        console.warn('[App] Failed to fetch player after retries:', error);
        return;
      }

      // Bail if auth user changed during the fetch
      if (auth.user?.id !== currentAuthId) return;

      setProfile((prev) => ({
        ...prev,
        name: data.name || prev.name,
        level: data.level ?? prev.level,
        exp: data.exp ?? prev.exp,
      }));
      localStorage.setItem('dicepit_playerId', data.id);
      setPlayerId(data.id);
      // Record the name that came from DB — we should NOT sync this back
      dbLoadedName.current = data.name || null;
      // Only allow name-sync for THIS auth user's profile
      nameLoadedForAuthId.current = currentAuthId;
    };

    fetchPlayer();
  }, [auth.isLoading, auth.user?.id]);

  // ── Trigger confetti once per account (first login after signup) ──────────
  useEffect(() => {
    if (!auth.isGuest && auth.user) {
      const confettiKey = `dicepit_confetti_shown_${auth.user.id}`;
      if (!localStorage.getItem(confettiKey)) {
        localStorage.setItem(confettiKey, '1');
        setShowConfetti(true);
      }
    }
  }, [auth.isGuest, auth.user?.id]);

  // ── Sync profile name to DB when authenticated user's name changes ───────
  // Guard: only sync if the profile was loaded for the CURRENT auth user,
  // AND the name actually differs from what we loaded from DB (to avoid
  // writing back the same value or a stale anonymous name).
  useEffect(() => {
    if (
      !auth.isGuest &&
      auth.user &&
      profile.name &&
      nameLoadedForAuthId.current === auth.user.id &&
      dbLoadedName.current !== null &&
      profile.name !== dbLoadedName.current
    ) {
      supabase
        .from('players')
        .update({ name: profile.name })
        .eq('auth_user_id', auth.user.id)
        .then(({ error }) => {
          if (error) console.warn('[Profile Sync] Failed to update name:', error.message);
          else dbLoadedName.current = profile.name; // update tracker after successful sync
        });
    }
  }, [profile.name, auth.isGuest, auth.user?.id]);

  return (
    <I18nProvider language={settings.language}>
      <CookieConsent />
      <Routes>
        {/* ── Menu ─────────────────────────────────────────────────────────── */}
        <Route
          path="/"
          element={
            <MenuPage
              profile={profile}
              currentSlogan={currentSlogan}
              expProgress={expProgress}
              expThreshold={expThreshold}
              settings={settings}
              isGuest={auth.isGuest}
              showConfetti={showConfetti}
              onConfettiDone={() => setShowConfetti(false)}
            />
          }
        />

        {/* ── Practice Lobby ───────────────────────────────────────────────── */}
        <Route
          path="/practice"
          element={<PracticePage settings={settings} />}
        />

        {/* ── Single Player ────────────────────────────────────────────────── */}
        <Route
          path="/play/:difficulty"
          element={
            <SinglePlayerPage
              profile={profile}
              settings={settings}
              addExp={addExp}
              expChange={expChange}
              setExpChange={setExpChange}
              isGuest={auth.isGuest}
            />
          }
        />

        {/* ── Multiplayer Lobby ────────────────────────────────────────────── */}
        <Route
          path="/multiplayer"
          element={
            <MultiplayerLobbyPage
              profile={profile}
              settings={settings}
              setPlayerId={setPlayerId}
              setRoomId={setRoomId}
            />
          }
        />

        {/* ── Multiplayer Room ─────────────────────────────────────────────── */}
        <Route
          path="/room/:roomId"
          element={
            <RoomPage
              profile={profile}
              settings={settings}
              roomId={roomId}
              playerId={playerId}
              setRoomId={setRoomId}
              setPlayerId={setPlayerId}
              refreshProfile={refreshProfile}
              refreshHistory={refreshHistory}
              authIsLoading={auth.isLoading}
              isGuest={auth.isGuest}
            />
          }
        />

        {/* ── Auth ──────────────────────────────────────────────────────────── */}
        <Route
          path="/auth"
          element={
            <AuthPage
              auth={auth}
              profile={profile}
              setProfile={setProfile}
            />
          }
        />

        {/* ── Reset Password ─────────────────────────────────────────────── */}
        <Route
          path="/auth/reset"
          element={<ResetPasswordPage auth={auth} />}
        />

        {/* ── Settings ─────────────────────────────────────────────────────── */}
        <Route
          path="/settings"
          element={
            <SettingsPage
              settings={settings}
              profile={profile}
              setSettings={setSettings}
              setProfile={setProfile}
              auth={auth}
            />
          }
        />

        {/* ── Leaderboard ──────────────────────────────────────────────────── */}
        <Route
          path="/leaderboard"
          element={<LeaderboardPage currentPlayerName={profile.name} />}
        />

        {/* ── History ──────────────────────────────────────────────────────── */}
        <Route
          path="/history"
          element={<HistoryPage history={history} historyLoading={historyLoading} />}
        />

        {/* ── How To Play ──────────────────────────────────────────────────── */}
        <Route path="/how-to-play" element={<HowToPlayPage />} />

        {/* ── Legal ──────────────────────────────────────────────────────────── */}
        <Route path="/legal" element={<LegalPage />} />

        {/* ── Security ─────────────────────────────────────────────────────── */}
        <Route path="/security" element={<SecurityPage />} />

        {/* ── Reset (hidden utility page) ──────────────────────────────────── */}
        <Route path="/reset" element={<ResetPage />} />

        {/* ── Tutorial ──────────────────────────────────────────────────────── */}
        <Route path="/tutorial" element={<TutorialGame isMuted={settings.isMuted} />} />

        {/* ── Fallback → Menu ──────────────────────────────────────────────── */}
        <Route path="*" element={<MenuPage profile={profile} currentSlogan={currentSlogan} expProgress={expProgress} expThreshold={expThreshold} settings={settings} isGuest={auth.isGuest} showConfetti={showConfetti} onConfettiDone={() => setShowConfetti(false)} />} />
      </Routes>
    </I18nProvider>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Page wrapper components
// ═══════════════════════════════════════════════════════════════════════════════

// ── MenuPage ─────────────────────────────────────────────────────────────────

interface MenuPageProps {
  profile: ReturnType<typeof useProfile>['profile'];
  currentSlogan: string;
  expProgress: number;
  expThreshold: number;
  settings: ReturnType<typeof useSettings>['settings'];
  isGuest: boolean;
  showConfetti?: boolean;
  onConfettiDone?: () => void;
}

const MenuPage: React.FC<MenuPageProps> = ({ profile, currentSlogan, expProgress, expThreshold, settings, isGuest, showConfetti, onConfettiDone }) => {
  const navigate = useNavigate();

  return (
    <Menu
      profile={profile}
      settings={settings}
      slogan={currentSlogan}
      expProgress={expProgress}
      expThreshold={expThreshold}
      onNavigate={(path: string) => navigate(path)}
      isGuest={isGuest}
      showConfetti={showConfetti}
      onConfettiDone={onConfettiDone}
    />
  );
};

// ── PracticePage ─────────────────────────────────────────────────────────────

interface PracticePageProps {
  settings: ReturnType<typeof useSettings>['settings'];
}

const PracticePage: React.FC<PracticePageProps> = ({ settings }) => {
  const navigate = useNavigate();

  return (
    <Menu
      profile={{ name: '', level: 0, exp: 0 }}
      settings={settings}
      slogan=""
      expProgress={0}
      expThreshold={0}
      onNavigate={(path: string) => navigate(path)}
      practiceMode={true}
    />
  );
};

// ── SinglePlayerPage ─────────────────────────────────────────────────────────

interface SinglePlayerPageProps {
  profile: ReturnType<typeof useProfile>['profile'];
  settings: ReturnType<typeof useSettings>['settings'];
  addExp: ReturnType<typeof useProfile>['addExp'];
  expChange: number | null;
  setExpChange: (v: number | null) => void;
  isGuest: boolean;
}

const SinglePlayerPage: React.FC<SinglePlayerPageProps> = ({
  profile,
  settings,
  addExp,
  expChange,
  setExpChange,
  isGuest,
}) => {
  const navigate = useNavigate();
  const { difficulty: diffParam } = useParams<{ difficulty: string }>();
  const [searchParams] = useSearchParams();

  const diff = (diffParam?.toUpperCase() || 'MEDIUM') as Difficulty;
  const timeoutParam = searchParams.get('timeout');
  const turnTimeout = timeoutParam ? Math.max(3, Math.min(15, parseInt(timeoutParam, 10) || 0)) : null;

  const {
    playerGrid,
    opponentGrid,
    currentDie,
    isDieRolling,
    gameState,
    message,
    lastAction,
    playerTotal,
    opponentTotal,
    resetGrids,
    placePlayerDie,
    startGame,
    turnTimer,
  } = useGame(diff, settings.isMuted, settings.diceSpeed, undefined, turnTimeout);

  // Start game on mount
  useEffect(() => {
    setExpChange(null);
    trackEvent('practice_start', { difficulty: diff, timeout: turnTimeout ?? 0 });
    const timer = setTimeout(() => startGame(), 0);
    return () => clearTimeout(timer);
  }, []);

  const handleReset = useCallback(() => {
    resetGrids();
    setExpChange(null);
    navigate('/');
  }, [resetGrids, setExpChange, navigate]);

  return (
    <Game
      playerGrid={playerGrid}
      opponentGrid={opponentGrid}
      currentDie={currentDie}
      isDieRolling={isDieRolling}
      gameState={gameState}
      message={message}
      lastAction={lastAction}
      playerTotal={playerTotal}
      opponentTotal={opponentTotal}
      expChange={expChange}
      profile={profile}
      difficulty={diff}
      onPlaceDie={placePlayerDie}
      onReset={handleReset}
      isMultiplayer={false}
      turnTimer={turnTimer}
      turnTimerMax={turnTimeout ?? 3}
      isGuest={isGuest}
      onSignUp={() => navigate('/auth')}
    />
  );
};

// ── MultiplayerLobbyPage ─────────────────────────────────────────────────────

interface MultiplayerLobbyPageProps {
  profile: ReturnType<typeof useProfile>['profile'];
  settings: ReturnType<typeof useSettings>['settings'];
  setPlayerId: (id: string | null) => void;
  setRoomId: (id: string | null) => void;
}

const MultiplayerLobbyPage: React.FC<MultiplayerLobbyPageProps> = ({
  profile,
  settings,
  setPlayerId,
  setRoomId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();
  const roomError = (location.state as { roomError?: string } | null)?.roomError || '';
  const [showTutorialGate, setShowTutorialGate] = useState(() => !isTutorialCompleted());

  const handleCreateRoom = async (gameMode: 'normal' | 'super-dice') => {
    const result = await api.createRoom('pvp', gameMode);
    console.log('[App] Room created:', result);
    trackEvent('multiplayer_create_room', { gameMode });
    setPlayerId(result.playerId);
    setRoomId(result.roomId);
    navigate(`/room/${result.roomId}`);
  };

  const handleJoinRoom = async (code: string) => {
    const response = await api.joinRoom(code);
    console.log('[App] Joined room:', response);
    trackEvent('multiplayer_join_room');
    setPlayerId(response.playerId);
    setRoomId(response.roomId);
    navigate(`/room/${response.roomId}`);
  };

  const handleQuickMatch = async (gameMode: 'normal' | 'super-dice') => {
    const result = await api.quickMatch(gameMode);
    console.log('[App] Quick match result:', result);
    trackEvent('multiplayer_quick_match', { gameMode });
    setPlayerId(result.playerId);
    setRoomId(result.roomId);
    navigate(`/room/${result.roomId}`);
  };

  return (
    <>
      <Menu
        profile={profile}
        settings={settings}
        slogan=""
        expProgress={0}
        expThreshold={0}
        onNavigate={(path: string) => navigate(path)}
        multiplayerMode={true}
        onStartMultiplayer={handleCreateRoom}
        onJoinRoom={handleJoinRoom}
        onQuickMatch={handleQuickMatch}
        initialError={roomError}
      />

      {/* Tutorial gate popup — same style as rematch/leave popups */}
      {showTutorialGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="pixel-card bg-[#1a101f] border-2 border-theme/50 p-6 lg:p-10 flex flex-col items-center gap-4 lg:gap-6 max-w-xs lg:max-w-sm w-full mx-4">
            <h2 className="text-sm lg:text-xl font-bold text-theme uppercase tracking-widest text-center">
              {t.tutMpTitle}
            </h2>
            <p className="text-[8px] lg:text-xs text-gray-400 text-center uppercase leading-relaxed">
              {t.tutMpText}
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => {
                  markTutorialSkipped();
                  setShowTutorialGate(false);
                  trackEvent('tutorial_gate_skip');
                }}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-black/60 text-[8px] lg:text-xs text-gray-400 font-bold uppercase hover:bg-black/80 transition-all"
              >
                {t.tutMpSkip}
              </button>
              <button
                onClick={() => {
                  trackEvent('tutorial_gate_play');
                  navigate('/tutorial');
                }}
                className="flex-1 px-3 py-2 lg:px-6 lg:py-3 pixel-border bg-theme text-[8px] lg:text-xs text-black font-bold uppercase hover:brightness-110 transition-all"
              >
                {t.tutMpPlay}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── RoomPage ─────────────────────────────────────────────────────────────────

interface RoomPageProps {
  profile: ReturnType<typeof useProfile>['profile'];
  settings: ReturnType<typeof useSettings>['settings'];
  roomId: string | null;
  playerId: string | null;
  setRoomId: (id: string | null) => void;
  setPlayerId: (id: string | null) => void;
  refreshProfile: ReturnType<typeof useProfile>['refreshProfile'];
  refreshHistory: () => void;
  authIsLoading: boolean;
  isGuest: boolean;
}

const RoomPage: React.FC<RoomPageProps> = ({
  profile,
  settings,
  roomId: stateRoomId,
  playerId,
  setRoomId,
  setPlayerId,
  refreshProfile,
  refreshHistory,
  authIsLoading,
  isGuest,
}) => {
  const navigate = useNavigate();
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();
  // The effective roomId: use state if set, otherwise from URL
  const effectiveRoomId = stateRoomId || urlRoomId || null;

  // Hook must be called unconditionally (Rules of Hooks).
  // Pass null roomId/playerId while auth is loading so it stays idle.
  const {
    isConnected: mpIsConnected,
    gameState: mpGameState,
    players: mpPlayers,
    playerNumber: mpPlayerNumber,
    joinedPlayerId: mpJoinedPlayerId,
    countdownNumber: mpCountdownNumber,
    error: mpError,
    isLoading: mpIsLoading,
    isDieRolling: mpIsDieRolling,
    turnTimer: mpTurnTimer,
    turnTimerMax: mpTurnTimerMax,
    chatMessages: mpChatMessages,
    placeDice: mpPlaceDice,
    rematchState: mpRematchState,
    roomExpired: mpRoomExpired,
    setReady: mpSetReady,
    sendChat: mpSendChat,
    requestRematch: mpRequestRematch,
    acceptRematch: mpAcceptRematch,
    declineRematch: mpDeclineRematch,
    disconnect: mpDisconnect,
    selectJokerValue: mpSelectJokerValue,
    selectShieldColumn: mpSelectShieldColumn,
  } = useMultiplayer(
    authIsLoading ? null : effectiveRoomId,
    authIsLoading ? null : playerId,
    profile.name,
    settings.isMuted
  );

  const resetGame = useCallback(() => {
    mpDisconnect();
    setRoomId(null);
    setPlayerId(null);
    navigate('/');
  }, [mpDisconnect, setRoomId, setPlayerId, navigate]);

  // Leave mid-game: server handles DEFEAT penalty via handleGameOver
  const leaveGame = useCallback(() => {
    trackEvent('multiplayer_leave_game');
    mpDisconnect();
    // Refresh profile after a short delay so DB write completes
    setTimeout(() => { refreshProfile(); refreshHistory(); }, 500);
    setRoomId(null);
    setPlayerId(null);
    navigate('/');
  }, [mpDisconnect, refreshProfile, refreshHistory, setRoomId, setPlayerId, navigate]);

  // ── Refresh profile from DB on multiplayer game over ─────────────────
  // Server already calls record_game_result — we just refresh to get updated values.
  const [mpExpChange, setMpExpChange] = useState<number | null>(null);
  const expAwardedRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (mpGameState?.gameStatus !== 'game_over' || !mpGameState.winner) {
      if (mpGameState?.gameStatus !== 'game_over') {
        expAwardedRef.current = null;
        setMpExpChange(null);
      }
      return;
    }

    const outcomeKey = `${effectiveRoomId}-${mpGameState.winner}-${mpGameState.playerScore}-${mpGameState.opponentScore}`;
    if (expAwardedRef.current === outcomeKey) return;
    expAwardedRef.current = outcomeKey;

    const myPlayerKey = mpPlayerNumber === 1 ? 'player1' : 'player2';
    const myScore = mpPlayerNumber === 1 ? (mpGameState.playerScore || 0) : (mpGameState.opponentScore || 0);
    const theirScore = mpPlayerNumber === 1 ? (mpGameState.opponentScore || 0) : (mpGameState.playerScore || 0);

    let outcome: 'VICTORY' | 'DEFEAT' | 'DRAW';
    if (mpGameState.winner === myPlayerKey) {
      outcome = 'VICTORY';
    } else if (mpGameState.winner === null) {
      outcome = 'DRAW';
    } else {
      outcome = 'DEFEAT';
    }

    // Optimistic EXP estimate for immediate UI display
    const level = profile.level;
    let gained = 0;
    if (outcome === 'VICTORY') {
      const diffBonus = level <= 5 ? 50 : level <= 10 ? 100 : 200;
      gained = diffBonus + Math.max(0, myScore - theirScore) * 2;
    } else if (outcome === 'DEFEAT') {
      gained = -40;
    } else {
      gained = 10;
    }
    setMpExpChange(gained);

    // Refresh profile & history from DB (server already recorded the result)
    // Small delay to let the server's DB write complete
    setTimeout(() => { refreshProfile(); refreshHistory(); }, 500);

    trackEvent('multiplayer_game_over', { outcome, my_score: myScore, their_score: theirScore, exp_change: gained });
    console.log(`[RoomPage] Game over: ${outcome}, refreshing profile from DB`);
  }, [mpGameState?.gameStatus, mpGameState?.winner]);

  // If we arrived via URL (no stateRoomId), just set the roomId.
  // useMultiplayer will handle the actual join_room via socket.
  useEffect(() => {
    if (authIsLoading) return;
    if (urlRoomId && !stateRoomId) {
      console.log('[RoomPage] Setting roomId from URL:', urlRoomId);
      setRoomId(urlRoomId);
    }
  }, [urlRoomId, stateRoomId, authIsLoading]);

  // When useMultiplayer joins successfully, capture the playerId
  useEffect(() => {
    if (mpJoinedPlayerId && !playerId) {
      console.log('[RoomPage] Got playerId from useMultiplayer:', mpJoinedPlayerId);
      setPlayerId(mpJoinedPlayerId);
      refreshProfile();
    }
  }, [mpJoinedPlayerId]);

  // If useMultiplayer reports an error (room full, not found, etc.), redirect
  useEffect(() => {
    if (mpError && !mpGameState) {
      console.error('[RoomPage] Join error, redirecting:', mpError);
      const userMsg = mpError.includes('full') ? t.roomIsFull
        : mpError.includes('started') ? t.gameAlreadyStartedUpper
          : mpError.includes('completed') ? t.roomNoLongerExistsUpper
            : t.roomNotFoundUpper;
      navigate('/multiplayer', { state: { roomError: userMsg } });
    }
  }, [mpError]);

  // If the room expired due to idle timeout, redirect to multiplayer lobby with warning
  useEffect(() => {
    if (mpRoomExpired) {
      console.log('[RoomPage] Room expired, redirecting to lobby:', mpRoomExpired);
      mpDisconnect();
      setRoomId(null);
      setPlayerId(null);
      navigate('/multiplayer', { state: { roomError: mpRoomExpired } });
    }
  }, [mpRoomExpired]);

  // Determine turn
  const isMyTurn =
    (mpGameState?.currentPlayer === 'player1' && mpPlayerNumber === 1) ||
    (mpGameState?.currentPlayer === 'player2' && mpPlayerNumber === 2);

  // Show grids from MY perspective
  const myGrid = mpPlayerNumber === 1
    ? (mpGameState?.playerGrid || INITIAL_GRID)
    : (mpGameState?.opponentGrid || INITIAL_GRID);
  const theirGrid = mpPlayerNumber === 1
    ? (mpGameState?.opponentGrid || INITIAL_GRID)
    : (mpGameState?.playerGrid || INITIAL_GRID);
  const myScore = mpPlayerNumber === 1
    ? (mpGameState?.playerScore || 0)
    : (mpGameState?.opponentScore || 0);
  const theirScore = mpPlayerNumber === 1
    ? (mpGameState?.opponentScore || 0)
    : (mpGameState?.playerScore || 0);

  const t = useTranslation();

  // Show loading while auth is initializing or socket is connecting
  if (authIsLoading || mpIsLoading) {
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center text-white bg-[#1a101f]">
        <div className="text-2xl font-bold text-theme animate-pulse tracking-wider uppercase mb-4">
          {t.dicepit}
        </div>
        <div className="text-xs text-gray-400 animate-pulse uppercase tracking-widest">
          {t.joiningRoom}
        </div>
      </div>
    );
  }

  return (
    <Game
      playerGrid={myGrid}
      opponentGrid={theirGrid}
      currentDie={mpGameState?.currentDie || null}
      isDieRolling={mpIsDieRolling}
      gameState={mpGameState?.gameStatus || 'waiting'}
      message={
        mpError
          ? mpError
          : mpIsLoading
            ? t.connecting
            : !mpIsConnected
              ? t.connecting
              : mpGameState?.gameStatus === 'rolling' && isMyTurn
                ? t.rolling
                : mpGameState?.gameStatus === 'rolling' && !isMyTurn
                  ? t.opponentRolling
                  : mpGameState?.gameStatus === 'placing' && isMyTurn
                    ? t.placeDie(mpTurnTimer)
                    : mpGameState?.gameStatus === 'placing' && !isMyTurn
                      ? t.opponentPlacing(mpTurnTimer)
                      : mpGameState?.message || t.waitingForOpponent
      }
      lastAction={mpGameState?.lastAction || null}
      playerTotal={myScore}
      opponentTotal={theirScore}
      expChange={mpExpChange}
      profile={profile}
      difficulty="MEDIUM"
      gameMode={mpGameState?.gameMode || 'normal'}
      shields={mpGameState?.shields || undefined}
      onPlaceDie={mpPlaceDice}
      onSelectJokerValue={mpSelectJokerValue}
      onSelectShieldColumn={mpSelectShieldColumn}
      onReset={resetGame}
      onLeaveGame={leaveGame}
      isMultiplayer={true}
      isConnected={mpIsConnected}
      players={mpPlayers}
      playerNumber={mpPlayerNumber}
      onSetReady={mpSetReady}
      roomId={effectiveRoomId}
      countdownNumber={mpCountdownNumber ?? null}
      isMyTurn={isMyTurn}
      chatMessages={mpChatMessages}
      onSendChat={mpSendChat}
      turnTimer={mpTurnTimer}
      turnTimerMax={mpTurnTimerMax}
      rematchState={mpRematchState}
      onRequestRematch={mpRequestRematch}
      onAcceptRematch={mpAcceptRematch}
      onDeclineRematch={mpDeclineRematch}
      isGuest={isGuest}
      onSignUp={() => navigate('/auth')}
    />
  );
};

// ── AuthPage ─────────────────────────────────────────────────────────────────

interface AuthPageProps {
  auth: ReturnType<typeof useAuth>;
  profile: ReturnType<typeof useProfile>['profile'];
  setProfile: ReturnType<typeof useProfile>['setProfile'];
}

const AuthPage: React.FC<AuthPageProps> = ({ auth, profile, setProfile }) => {
  const navigate = useNavigate();
  const t = useTranslation();

  const handleSignUp = async (data: { email: string; password: string; username: string }) => {
    const result = await auth.signUp(data);
    // After signup, link the guest player to the new auth user
    const authUserId = result?.user?.id;
    const guestPlayerId = localStorage.getItem('dicepit_playerId');
    if (guestPlayerId && authUserId) {
      try {
        await auth.linkGuestPlayer(guestPlayerId, authUserId);
      } catch (e: unknown) {
        // If username is taken (unique constraint violation), throw to show error
        const msg = e instanceof Error ? e.message : '';
        const code = (e as { code?: string })?.code;
        if (msg.includes('unique') || msg.includes('duplicate') || code === '23505') {
          throw new Error(t.usernameTaken);
        }
        console.warn('[Auth] Could not link guest player:', e);
      }
    }
    // Set the chosen username locally immediately
    setProfile((prev) => ({ ...prev, name: data.username.toUpperCase() }));
    trackEvent('auth_sign_up');
    // Navigate home immediately (no email confirmation needed)
    navigate('/');
  };

  const handleSignIn = async (data: { email: string; password: string }) => {
    // Save the anonymous player ID before sign-in replaces the session.
    // We need this to clean up the orphaned anonymous player row afterwards.
    const anonPlayerId = localStorage.getItem('dicepit_playerId');

    await auth.signIn(data);

    // Clear the stale anonymous player ID so the profile-fetching useEffect
    // picks up the correct player for the authenticated user (not the anonymous one).
    localStorage.removeItem('dicepit_playerId');

    // Clean up the orphaned anonymous player row in the background via RPC
    // (SECURITY DEFINER function that safely verifies the row is anonymous)
    if (anonPlayerId) {
      supabase
        .rpc('cleanup_anonymous_player', { anon_player_id: anonPlayerId })
        .then(({ error }) => {
          if (error) console.warn('[Auth] Failed to clean up anonymous player:', error.message);
          else console.log('[Auth] Cleaned up orphaned anonymous player:', anonPlayerId);
        });
    }

    trackEvent('auth_sign_in');
    navigate('/');
  };

  return (
    <Auth
      onBack={() => navigate('/')}
      onSignUp={handleSignUp}
      onSignIn={handleSignIn}
      onResetPassword={auth.resetPassword}
      defaultUsername={profile.name}
    />
  );
};

// ── ResetPasswordPage ────────────────────────────────────────────────────────

interface ResetPasswordPageProps {
  auth: ReturnType<typeof useAuth>;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ auth }) => {
  const navigate = useNavigate();

  return (
    <ResetPassword
      onUpdatePassword={auth.updatePassword}
      onBack={() => navigate('/')}
    />
  );
};

// ── SettingsPage ─────────────────────────────────────────────────────────────

interface SettingsPageProps {
  settings: ReturnType<typeof useSettings>['settings'];
  profile: ReturnType<typeof useProfile>['profile'];
  setSettings: ReturnType<typeof useSettings>['setSettings'];
  setProfile: ReturnType<typeof useProfile>['setProfile'];
  auth: ReturnType<typeof useAuth>;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ settings, profile, setSettings, setProfile, auth }) => {
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    const sock = await getSocket();
    return new Promise<void>((resolve, reject) => {
      sock.emit('delete_account', {} as Record<string, never>, async (res: { success: boolean; error?: string }) => {
        if (res.success) {
          trackEvent('account_deleted');
          // Sign out from Supabase first to cleanly tear down the auth session
          // and prevent AbortError from lingering auth listeners
          try { await supabase.auth.signOut(); } catch { /* user already deleted */ }
          disconnectSocket();
          localStorage.clear();
          // Small delay to let cleanup finish before reload
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
          resolve();
        } else {
          reject(new Error(res.error || 'Failed to delete account'));
        }
      });
    });
  };

  return (
    <Settings
      settings={settings}
      profile={profile}
      onUpdateSettings={setSettings}
      onUpdateProfile={(name) => setProfile((p) => ({ ...p, name }))}
      onBack={() => navigate('/')}
      isGuest={auth.isGuest}
      userEmail={auth.user?.email}
      onSignOut={auth.signOut}
      onNavigateAuth={() => navigate('/auth')}
      onDeleteAccount={handleDeleteAccount}
    />
  );
};

// ── LeaderboardPage ──────────────────────────────────────────────────────────

interface LeaderboardPageProps {
  currentPlayerName: string;
}

const LeaderboardPage: React.FC<LeaderboardPageProps> = ({ currentPlayerName }) => {
  const navigate = useNavigate();

  return (
    <Leaderboard
      currentPlayerName={currentPlayerName}
      onBack={() => navigate('/')}
    />
  );
};

// ── HistoryPage ──────────────────────────────────────────────────────────────

interface HistoryPageProps {
  history: ReturnType<typeof useHistory>['history'];
  historyLoading: boolean;
}

const HistoryPage: React.FC<HistoryPageProps> = ({ history, historyLoading }) => {
  const navigate = useNavigate();

  return (
    <History
      history={history}
      loading={historyLoading}
      onBack={() => navigate('/')}
    />
  );
};

// ── HowToPlayPage ────────────────────────────────────────────────────────────

const HowToPlayPage: React.FC = () => {
  const navigate = useNavigate();
  return <HowToPlay onBack={() => navigate('/')} />;
};

// ── LegalPage ────────────────────────────────────────────────────────────────

const LegalPage: React.FC = () => {
  const navigate = useNavigate();
  return <Legal onBack={() => navigate('/')} />;
};

// ── SecurityPage ─────────────────────────────────────────────────────────────

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  return <Security onBack={() => navigate('/')} />;
};

// ── ResetPage (hidden utility) ──────────────────────────────────────────────

const ResetPage: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    localStorage.clear();
    setShow(true);
    const timer = setTimeout(() => navigate('/'), 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="h-[100dvh] flex items-center justify-center bg-[#1a101f] relative">
      {show && (
        <div className="pixel-border bg-black/80 px-8 py-6 text-center animate-pulse">
          <div className="text-green-400 text-xs lg:text-sm font-bold uppercase tracking-widest mb-2">
            {t.userDataReset}
          </div>
          <div className="text-gray-500 text-[8px] lg:text-xs uppercase tracking-wider">
            {t.redirecting}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
